import { InfiniteScroll } from "@/components/InfiniteScroll";
import { Input } from "@/components/ui/input";
import { AdminPageLayout } from "@/layouts/AdminPageLayout";
import { getSpecialAccessUsers, updateSpecialAccessUser, deleteSpecialAccessUser } from "@/services/admin/specialAccessUser.service";
import { SpecialAccessUser, UserStatus } from "@/services/admin/specialAccessUser.interfaces";
import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAppToast } from "@/hooks/useAppToast";
import { UserCard } from "@/components/cards/UserCard";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";

const Toolbar: React.FC<{ onSearch: (value: string) => void }> = ({ onSearch }) => (
  <section id="tools" className="mb-6">
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div className="flex flex-1 items-center space-x-4">
        <div className="relative flex-1 max-w-xl">
          <Input
            type="search"
            placeholder="Search users..."
            className="pl-10"
            onChange={(e) => onSearch(e.target.value)}
          />
          <i className="fa-solid fa-search absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"></i>
        </div>
        <button 
          className="flex items-center space-x-2 rounded-lg border border-neutral-300 px-4 py-2"
          onClick={() => console.log("Filter clicked")}
        >
          <i className="fa-solid fa-filter text-neutral-600"></i>
          <span>Filters</span>
        </button>
      </div>
    </div>
  </section>
);

const SpecialAccessUserManagementPage: React.FC = () => {
  const [searchText, setSearchText] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0); // Adicionado para forçar recarga
  const toast = useAppToast();
  const navigate = useNavigate();

  const handleEdit = (userId: string) => {
    navigate(`/admin/access/users/${userId}`);
  };

  const handleStatusChange = async (userId: string, status: UserStatus) => {
    try {
      await updateSpecialAccessUser(userId, { status });
      toast.success("User status updated");
      setRefreshKey(prev => prev + 1); // Força recarga após atualização
    } catch (error) {
      toast.error("Failed to update user status");
      console.error("Failed to update user status:", error);
    }
  };

  const handleDeleteClick = (userId: string) => {
    setUserToDelete(userId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    try {
      await deleteSpecialAccessUser(userToDelete);
      toast.success("User deleted");
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      setRefreshKey(prev => prev + 1); // Força recarga após deleção
    } catch (error) {
      toast.error("Failed to delete user");
      console.error("Failed to delete user:", error);
    }
  };

  const fetchUsers = useCallback(async (page: number) => {
    try {
      const response = await getSpecialAccessUsers(
        { search: searchText },
        page,
        10
      );
      return response.data;
    } catch (error) {
      toast.error("Failed to load users");
      console.error("Error loading users:", error);
      return [];
    }
  }, [searchText, toast]);

  const handleSearch = (value: string) => {
    setSearchText(value);
    setRefreshKey(prev => prev + 1); // Força recarga ao pesquisar
  };

  return (
    <AdminPageLayout title="Special Access User Management">
      <main id="main" className="py-6">
        <Toolbar onSearch={handleSearch} />

        <InfiniteScroll
          key={refreshKey} // Força remontagem do componente
          fetchItems={fetchUsers}
          renderItem={(user: SpecialAccessUser) => (
            <UserCard 
              key={user.id}
              user={{
                ...user,
                enrollmentDate: user.enrollmentDate || 'N/A'
              }}
              userId={user.id}
              onEdit={handleEdit}
              onStatusChange={handleStatusChange}
              onDelete={handleDeleteClick}
              className="mb-4"
            />
          )}
          itemKey={(user) => user.id}
          layout="grid"
          gridCols={{ default: 2, lg: 3 }}
        />

        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this user? This action cannot be undone.
            </DialogDescription>
            <div className="mt-4 flex justify-end space-x-2">
              <button
                className="rounded-lg bg-neutral-100 px-4 py-2 hover:bg-neutral-200"
                onClick={() => setDeleteDialogOpen(false)}
              >
                Cancel
              </button>
              <button
                className="rounded-lg bg-red-500 px-4 py-2 text-white hover:bg-red-600"
                onClick={handleDeleteConfirm}
              >
                Delete
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </AdminPageLayout>
  );
};

export default SpecialAccessUserManagementPage;
